import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, GripVertical, X } from "lucide-react";
import { ApiService } from "../../../services/api";
import { useModal } from "../../../hooks/useModal";

interface Question {
  id: string;
  title: string;
  description?: string;
  type: string;
  isMandatory: boolean;
  options?: any[];
  metadata?: any;
  order: number;
}

interface QuestionManagerProps {
  surveyId: string;
  sectionId: string;
  canUpdateSurvey: boolean;
}

const QUESTION_TYPES = [
  { value: "TEXT", label: "Metin" },
  { value: "RADIO", label: "Tek Seçim" },
  { value: "CHECKBOX", label: "Çoklu Seçim" },
  { value: "FILE", label: "Dosya" },
];

export default function QuestionManager({ surveyId, sectionId, canUpdateSurvey }: QuestionManagerProps) {
  const { showAlert, showConfirm } = useModal();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [creatingQuestion, setCreatingQuestion] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [savingQuestion, setSavingQuestion] = useState<string | null>(null);

  const [questionForm, setQuestionForm] = useState({
    title: "",
    description: "",
    type: "TEXT",
    isMandatory: false,
    options: [""],
    metadata: {},
  });

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getSurveyQuestions(surveyId, sectionId);
      const questionsList = Array.isArray(response) ? response : response?.data || [];
      const sortedQuestions = questionsList.sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
      setQuestions(sortedQuestions);
    } catch (error: any) {
      console.error("Sorular yüklenirken hata:", error);
      await showAlert({
        title: "Hata",
        message: "Sorular yüklenirken hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sectionId) {
      loadQuestions();
    }
  }, [sectionId]);

  const resetQuestionForm = () => {
    setQuestionForm({
      title: "",
      description: "",
      type: "TEXT",
      isMandatory: false,
      options: [""],
      metadata: {},
    });
  };

  const handleQuestionSave = async () => {
    if (!questionForm.title.trim()) {
      await showAlert({
        title: "Uyarı",
        message: "Soru başlığı zorunludur.",
        type: "warning",
      });
      return;
    }

    const hasOptionsType = ["RADIO", "CHECKBOX"].includes(questionForm.type);
    try {
      setSavingQuestion(editingQuestion || "creating");

      const payload: any = {
        title: questionForm.title.trim(),
        type: questionForm.type,
        isMandatory: questionForm.isMandatory,
        metadata: questionForm.metadata,
        order: editingQuestion
          ? questions.find((q) => q.id === editingQuestion)?.order ?? questions.length + 1
          : questions.length + 1,
      };

      if (questionForm.description.trim()) {
        payload.description = questionForm.description.trim();
      }

      if (hasOptionsType) {
        payload.options = questionForm.options
          .filter((opt) => opt.trim())
          .map((opt) => ({
            label: opt.trim(),
            value: opt.trim(),
          }));
      }

      if (editingQuestion) {
        await ApiService.updateSurveyQuestion(surveyId, sectionId, editingQuestion, payload);
      } else {
        await ApiService.createSurveyQuestion(surveyId, sectionId, payload);
      }

      setEditingQuestion(null);
      setCreatingQuestion(false);
      resetQuestionForm();
      await loadQuestions();
    } catch (error: any) {
      console.error("Soru kaydetme hatası:", error);
      await showAlert({
        title: "Hata",
        message: error.message || "Soru kaydedilirken hata oluştu.",
        type: "error",
      });
    } finally {
      setSavingQuestion(null);
    }
  };

  const handleEditQuestion = (question: Question) => {
    if (!canUpdateSurvey) {
      showAlert({
        title: "Yetki Hatası",
        message: "Anket güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    setEditingQuestion(question.id);
    setCreatingQuestion(false);
    setQuestionForm({
      title: question.title,
      description: question.description || "",
      type: question.type,
      isMandatory: question.isMandatory,
      options: question.options?.map((opt: any) => opt.label || opt.value || opt) || [""],
      metadata: question.metadata || {},
    });
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!canUpdateSurvey) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Anket güncelleme yetkiniz bulunmamaktadır.",
        type: "error",
      });
      return;
    }

    const question = questions.find((q) => q.id === questionId);
    const confirmed = await showConfirm({
      title: "Soruyu Sil",
      message: `"${question?.title}" sorusunu silmek istediğinizden emin misiniz?`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      try {
        setDeletingQuestion(questionId);
        await ApiService.deleteSurveyQuestion(surveyId, sectionId, questionId);
        await loadQuestions();
      } catch (error: any) {
        console.error("Soru silme hatası:", error);
        await showAlert({
          title: "Hata",
          message: error.message || "Soru silinirken hata oluştu.",
          type: "error",
        });
      } finally {
        setDeletingQuestion(null);
      }
    }
  };

  const reorderQuestions = (fromIndex: number, toIndex: number) => {
    if (!canUpdateSurvey) return;

    const newQuestions = [...questions];
    const [reorderedItem] = newQuestions.splice(fromIndex, 1);
    newQuestions.splice(toIndex, 0, reorderedItem);

    const updatedQuestions = newQuestions.map((question, index) => ({
      ...question,
      order: index,
    }));

    setQuestions(updatedQuestions);

    updatedQuestions.forEach(async (question, index) => {
      try {
        await ApiService.updateSurveyQuestion(surveyId, sectionId, question.id, {
          ...question,
          order: index,
        });
      } catch (error) {
        console.error("Soru sırası güncellenirken hata:", error);
      }
    });
  };

  const handleDragStart = (index: number) => setDragIndex(index);
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();
  const handleDrop = (index: number) => {
    if (dragIndex !== null && dragIndex !== index) {
      reorderQuestions(dragIndex, index);
    }
    setDragIndex(null);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = value;
    setQuestionForm((prev) => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    setQuestionForm((prev) => ({
      ...prev,
      options: [...prev.options, ""],
    }));
  };

  const removeOption = (index: number) => {
    if (questionForm.options.length > 1) {
      const newOptions = questionForm.options.filter((_, i) => i !== index);
      setQuestionForm((prev) => ({ ...prev, options: newOptions }));
    }
  };

  const handleCancelForm = () => {
    setEditingQuestion(null);
    setCreatingQuestion(false);
    resetQuestionForm();
  };

  const renderQuestionForm = (title: string, containerClassName = "bg-white border border-gray-200 rounded-lg p-6 shadow-sm") => (
    <div className={containerClassName}>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">{title}</h4>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Soru Başlığı *</label>
          <input
            type="text"
            value={questionForm.title}
            onChange={(e) =>
              setQuestionForm((prev) => ({
                ...prev,
                title: e.target.value,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Örn: İş ortamından ne kadar memnunsunuz?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
          <textarea
            value={questionForm.description}
            onChange={(e) =>
              setQuestionForm((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Örn: Lütfen 1-5 arasında bir değerlendirme yapın."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Soru Tipi *</label>
          <select
            value={questionForm.type}
            onChange={(e) => setQuestionForm((prev) => ({ ...prev, type: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer hover:border-gray-400 transition-colors">
            {QUESTION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {["RADIO", "CHECKBOX"].includes(questionForm.type) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Seçenekler *</label>
            <div className="space-y-2">
              {questionForm.options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Seçenek ${index + 1}`}
                  />
                  {questionForm.options.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addOption} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                + Seçenek Ekle
              </button>
            </div>
          </div>
        )}

        <label
          htmlFor={`isMandatory_${sectionId}_${editingQuestion || "new"}`}
          className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${questionForm.isMandatory ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300 bg-white"
            }`}>
          <input
            type="checkbox"
            id={`isMandatory_${sectionId}_${editingQuestion || "new"}`}
            checked={questionForm.isMandatory}
            onChange={(e) =>
              setQuestionForm((prev) => ({
                ...prev,
                isMandatory: e.target.checked,
              }))
            }
            className="w-5 h-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div className="flex-1">
            <div className="font-medium text-gray-900 text-sm">Bu Soru Zorunludur</div>
            <div className="text-xs text-gray-500 mt-1">Kullanıcılar bu soruyu cevaplamadan anketi tamamlayamaz.</div>
          </div>
        </label>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancelForm}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
            İptal
          </button>
          <button
            type="button"
            onClick={handleQuestionSave}
            disabled={savingQuestion !== null}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {savingQuestion !== null && (
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
            )}
            {savingQuestion !== null ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {canUpdateSurvey && !creatingQuestion && !editingQuestion && (
        <button
          onClick={() => {
            setEditingQuestion(null);
            setCreatingQuestion(true);
            resetQuestionForm();
          }}
          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600">
          <Plus className="w-5 h-5" />
          Yeni Soru Ekle
        </button>
      )}

      {creatingQuestion && renderQuestionForm("Yeni Soru")}

      {questions.length === 0 && !creatingQuestion ? (
        <div className="text-center py-8 text-gray-500">
          <p>Bu bölümde henüz soru bulunmuyor.</p>
          <p className="text-sm">Yukarıdaki butonu kullanarak ilk sorunuzu ekleyin.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((question, index) => {
            const isEditing = editingQuestion === question.id;

            return (
              <div
                key={question.id}
                className={`bg-white border rounded-lg p-4 transition-shadow ${isEditing ? "border-blue-200 ring-1 ring-blue-100" : "border-gray-200 hover:shadow-sm"}`}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}>
                {isEditing ? (
                  renderQuestionForm(`${index + 1}. Soruyu Düzenle`, "rounded-lg")
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {canUpdateSurvey && (
                        <div
                          className="cursor-move text-gray-400 hover:text-gray-600 pt-1"
                          draggable={true}
                          onDragStart={() => handleDragStart(index)}
                        >
                          <GripVertical className="w-4 h-4" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900 mb-1 break-all">
                              {index + 1}. {question.title}
                              {question.isMandatory && <span className="text-red-500 ml-1">*</span>}
                            </h5>
                            {question.description && (
                              <p className="text-sm text-gray-600 mb-2 whitespace-pre-line break-words">
                                {question.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                {QUESTION_TYPES.find((t) => t.value === question.type)?.label || question.type}
                              </span>
                              {question.isMandatory && <span className="text-red-600 font-medium">Zorunlu</span>}
                            </div>
                          </div>
                        </div>

                        {["RADIO", "CHECKBOX"].includes(question.type) && question.options && (
                          <div className="mt-3 pl-4 border-l-2 border-gray-100">
                            <p className="text-xs text-gray-500 mb-2">Seçenekler:</p>
                            <div className="space-y-1">
                              {question.options.map((option: any, optIndex: number) => (
                                <div key={optIndex} className="text-sm text-gray-700 break-all">
                                  • {option.label || option.value || option}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {canUpdateSurvey && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditQuestion(question)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Soruyu Düzenle">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteQuestion(question.id)}
                          disabled={deletingQuestion === question.id}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                          title="Soruyu Sil">
                          {deletingQuestion === question.id ? (
                            <div className="animate-spin h-4 w-4 border-2 rounded-full border-t-transparent border-red-600"></div>
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
